import { useEffect, useMemo, useState } from 'react'
import { Drawer, Button, Checkbox, Row, Col, Divider, Typography } from 'antd'
import { logUserEvent } from 'helpers/userEventLogger'
import SpacePicker from '../MoveAssetDrawer/SpacePicker/SpacePicker'
import { useOnlineContext } from 'helpers/contexts/OnlineContext/OnlineContext'
import { useEntityData } from 'EntityDataContext/EntityDataContext'
import { getCopyListOptions } from '../utils'

const CopyAssetsFromSpaceDrawer = ({
  open = false,
  currentSpace = {},
  onClose = () => {}
}) => {
  const {
    assets,
    assetsToCopyList,
    setAssetsToCopyList,
    userData: { email },
    syncStatusHook: { imagesToUploadCount }
  } = useEntityData()

  const { isOnline } = useOnlineContext()

  const [selectedSpace, setSelectedSpace] = useState(undefined)
  const [showNoAssetsMessage, setShowNoAssetsMessage] = useState(false)
  const [checkedList, setCheckedList] = useState([])
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)

  useEffect(() => {
    if (selectedSpace) {
      const selectedAssets = (assets.assetTree?.[selectedSpace.facilityId]?.[selectedSpace.floorId]?.[selectedSpace.spaceId] || []).map(assetId => {
        return assets.items[assetId]
      })

      if (selectedAssets.length) {
        setShowNoAssetsMessage(false)
        setAssetsToCopyList(selectedAssets)
      } else {
        setShowNoAssetsMessage(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpace])
  
  const availableAssets = getCopyListOptions(assetsToCopyList)

  const assetCheckboxList = useMemo(() => availableAssets.map((o) => (
    <Col span={24} key={o.value}>
      <Checkbox value={o.value} style={{ width: '100%' }}>
        {o.label}{' '}
        {o.description && (
          <Typography.Text
            style={{ maxWidth: 300 }}
            ellipsis={{
              tooltip: true,
            }}
          >
            {o.description ? ` - ${o.description}` : null}
          </Typography.Text>
        )}
      </Checkbox>
    </Col>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  )), [availableAssets, checkedList])

  function onSelectedLocation(space) {
    setAssetsToCopyList([])
    setSelectedSpace(space)
  }

  function onSelectAsset(list) {
    setCheckedList(list)
    setIndeterminate(!!list.length && list.length < availableAssets.length)
    setCheckAll(list.length === availableAssets.length)
  }

  function onCheckAllChange(e) {
    setCheckedList(e.target.checked ? availableAssets.map((v) => v.value) : [])
    setIndeterminate(false)
    setCheckAll(e.target.checked)
  }

  function onCloseDrawer(assets = undefined) {
    setSelectedSpace(undefined)
    setShowNoAssetsMessage(false)
    setCheckedList([])
    setIndeterminate(false)
    setCheckAll(false)

    logUserEvent({
      user: email,
      eventType: `Copy asset (from space) drawer closed - ${assets ? 'Assets selected = ' + assets : 'No assets selected'}`,
      imageOutboxSize: imagesToUploadCount
    }, isOnline)

    if (assets) {
      onClose(assets)
    } else {
      onClose()
    }
  }

  return (
    <Drawer
      title="Copy assets from space"
      placement="right"
      onClose={() => onCloseDrawer()}
      open={open}
      className="asset-type-drawer"
      closable={false}
      destroyOnClose
    >
      <SpacePicker
        label="Select space with assets to be copied"
        onChange={onSelectedLocation}
        space={selectedSpace}
        currentSpace={currentSpace}
      />
      {showNoAssetsMessage && (
        <div style={{ color: '#ff1945'}}>The selected space has no assets to copy</div>
      )}
      {assetsToCopyList.length > 0 && (
        <>
          <Checkbox
            checked={checkAll}
            indeterminate={indeterminate}
            onChange={onCheckAllChange}
          >
            Copy All Assets
          </Checkbox>
          <Divider />
          <Checkbox.Group value={checkedList} onChange={onSelectAsset}>
            <Row>{assetCheckboxList}</Row>
          </Checkbox.Group>
        </>
      )}
      <Divider />
      <div className="form-buttons">
        <Button
          type="transparent"
          onClick={() => onCloseDrawer()}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={checkedList.length === 0}
          onClick={() => onCloseDrawer(checkedList)}
        >
          Copy
        </Button>
      </div>
    </Drawer>
  )
}

export default CopyAssetsFromSpaceDrawer
